import React, { useEffect, useState } from "react";

/**
 * First-visit install prompt — Sean personally asks new visitors to install the app.
 *  - Shows ONCE per browser (localStorage flag).
 *  - Skips if already installed (standalone display mode).
 *  - Uses the captured `beforeinstallprompt` event for Chrome/Edge/Android.
 *  - Falls back to a friendly iOS instructions sheet on Safari.
 *  - Sean's voice: nerdy + chill + cheeky, ORANGE-led, mentions mac & cheese & basilisk.
 */

const SEAN_AVATAR = "/sean-avatar.jpg";
const FLAG_KEY = "seanlab_first_install_seen";
const SHOW_DELAY_MS = 1800;  // give the page a beat to render so Sean isn't blocking the hero

export default function FirstVisitInstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    // Skip if already standalone (installed) or already shown once.
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;
    if (localStorage.getItem(FLAG_KEY) === "1") return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const t = setTimeout(() => setShow(true), SHOW_DELAY_MS);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onPrompt);
    };
  }, []);

  const dismiss = (reason) => {
    localStorage.setItem(FLAG_KEY, "1");
    setShow(false);
    // Re-fire later if they install via header button instead — that's fine, flag is just for THIS prompt.
    void reason;
  };

  const tryInstall = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        await deferred.userChoice;
      } catch {/* ignore */}
      setDeferred(null);
      dismiss("installed");
      return;
    }
    // No native prompt available — show OS-specific instructions in-line.
    setShow(false);
    setTimeout(() => setShowHelp(true), 50);
  };

  const [showHelp, setShowHelp] = useState(false);
  const isIos = typeof window !== "undefined" &&
    /iPad|iPhone|iPod/.test(window.navigator.userAgent) &&
    !window.MSStream;

  if (!show && !showHelp) return null;

  return (
    <>
      {show && (
        <div
          className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center p-3 sm:p-4"
          style={{ background: "rgba(14,18,48,0.55)", backdropFilter: "blur(8px)" }}
          onClick={() => dismiss("backdrop")}
          data-testid="first-install-modal"
        >
          <div
            className="card max-w-md w-full animate-pop-in relative"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: "1.4rem 1.3rem 1.2rem", background: "#fff" }}
          >
            <button
              onClick={() => dismiss("close")}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white border-2 border-[var(--ink)] flex items-center justify-center text-lg"
              style={{ boxShadow: "2px 2px 0 0 var(--ink)" }}
              aria-label="Close"
              data-testid="first-install-close"
            >
              ×
            </button>

            {/* Sean header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="relative shrink-0">
                <img
                  src={SEAN_AVATAR}
                  alt="Sean"
                  className="rounded-full block animate-float"
                  style={{
                    width: 72, height: 72,
                    border: "3px solid var(--ink)",
                    boxShadow: "4px 4px 0 0 var(--ink)",
                    background: "#fff",
                    objectFit: "cover",
                  }}
                />
                <span
                  className="absolute"
                  style={{
                    right: -4, top: -4, background: "var(--orange)", color: "#fff",
                    fontSize: "0.6rem", padding: "2px 7px", borderRadius: 999,
                    border: "2px solid var(--ink)", fontWeight: 800, letterSpacing: "0.04em",
                  }}
                >
                  HI 👋
                </span>
              </div>
              <div className="min-w-0">
                <div className="eyebrow text-[var(--orange)]">a message from sean</div>
                <div className="font-display text-xl sm:text-2xl text-[var(--ink)] leading-tight">
                  Yo, Sciencer — install me real quick?
                </div>
              </div>
            </div>

            {/* Sean's cheeky pitch */}
            <div
              className="card-flat tint-orange"
              style={{ padding: "0.85rem 1rem", marginBottom: "1rem" }}
            >
              <p className="text-sm text-[var(--ink)] leading-relaxed m-0">
                Hey hey, I'm <strong>Sean</strong> 🧡. If you tap{" "}
                <strong>"⬇ Install Sean's Lab"</strong> below, my whole lab
                snaps onto your home screen like a real app — no app store, no
                downloads, no nonsense. Then I'm just one tap away whenever
                you want to race the clock or ask me something weird.
                <br /><br />
                <span style={{ color: "var(--ink-soft)" }}>
                  Fair warning: I will <em>absolutely</em> talk about mac &amp; cheese
                  and basilisk lizards if you let me. 🦎🧀
                </span>
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={tryInstall}
                className="btn w-full sm:flex-1"
                style={{ background: "var(--orange)", color: "#fff" }}
                data-testid="first-install-yes"
              >
                ⬇ Install Sean's Lab
              </button>
              <button
                onClick={() => dismiss("later")}
                className="btn-ghost w-full sm:w-auto text-center"
                data-testid="first-install-later"
              >
                Maybe later
              </button>
            </div>

            <div
              className="text-[0.68rem] font-mono mt-3 text-center"
              style={{ color: "var(--ink-soft)" }}
            >
              free · zero ads · zero tracking · just science 🧪
            </div>
          </div>
        </div>
      )}

      {showHelp && (
        <IosHelpSheet
          isIos={isIos}
          onClose={() => { setShowHelp(false); dismiss("help-closed"); }}
        />
      )}
    </>
  );
}

function IosHelpSheet({ isIos, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(14,18,48,0.55)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="first-install-help"
    >
      <div
        className="card max-w-md w-full tint-sun animate-pop-in relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white border-2 border-[var(--ink)] flex items-center justify-center text-lg"
          style={{ boxShadow: "2px 2px 0 0 var(--ink)" }}
        >
          ×
        </button>
        <div className="flex items-center gap-3 mb-3">
          <img
            src={SEAN_AVATAR}
            alt="Sean"
            className="rounded-2xl"
            style={{ width: 56, height: 56, border: "2.5px solid var(--ink)", boxShadow: "3px 3px 0 0 var(--ink)", objectFit: "cover", background: "#fff" }}
          />
          <div>
            <div className="eyebrow text-[var(--red)]">install</div>
            <div className="font-display text-xl text-[var(--ink)] leading-tight">
              Add Sean's Lab to your home screen
            </div>
          </div>
        </div>
        {isIos ? (
          <ol className="space-y-2 text-[var(--ink)] text-sm leading-relaxed pl-1">
            <li><strong>1.</strong> Tap the <strong>Share</strong> button at the bottom of Safari (the square with ⬆️).</li>
            <li><strong>2.</strong> Scroll and tap <strong>"Add to Home Screen"</strong>.</li>
            <li><strong>3.</strong> Tap <strong>Add</strong>. Sean lives on your phone now — for real.</li>
          </ol>
        ) : (
          <ol className="space-y-2 text-[var(--ink)] text-sm leading-relaxed pl-1">
            <li><strong>Chrome / Edge:</strong> tap the <strong>⋮</strong> menu → <strong>Install app</strong> (or the ⊕ icon in the address bar).</li>
            <li><strong>Android:</strong> tap <strong>⋮</strong> → <strong>Add to Home screen</strong>.</li>
            <li><strong>Tip:</strong> if it's not there yet, browse the lab for ~30s and try again — the browser unlocks Install after a tiny bit of use.</li>
          </ol>
        )}
        <button onClick={onClose} className="btn btn-red w-full mt-4 text-base">
          🧠 Got it — keep on sciencing!
        </button>
      </div>
    </div>
  );
}
